import { useEffect, useState } from "react";

import { Link } from "react-router-dom";
import { Clock, UserX, Stethoscope, Building2, CheckCircle2, MapPin } from "lucide-react";
import AuthImage from "./AuthImage";
import api from "../lib/api";

function initialsOf(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function formatBoardDate(iso) {
  // iso = "YYYY-MM-DD". Parseo manual para evitar corrimiento por zona horaria.
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  const dd = String(d).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${DAY_NAMES[dt.getDay()]} ${dd}/${mm}/${y}`;
}

const groupStyles = {
  late: {
    icon: Clock,
    label: "Llegaron tarde",
    dot: "bg-amber-400",
    ring: "ring-amber-300",
    text: "text-amber-600",
    chipBg: "bg-amber-50/70",
  },
  absent: {
    icon: UserX,
    label: "Ausentes",
    dot: "bg-red-400",
    ring: "ring-red-300",
    text: "text-red-600",
    chipBg: "bg-red-50/70",
  },
  incidents: {
    icon: Stethoscope,
    label: "Con incidencia",
    dot: "bg-sky-400",
    ring: "ring-sky-300",
    text: "text-sky-600",
    chipBg: "bg-sky-50/70",
  },
  punctual: {
    icon: CheckCircle2,
    label: "Puntuales",
    dot: "bg-emerald-400",
    ring: "ring-emerald-300",
    text: "text-emerald-600",
    chipBg: "bg-emerald-50/70",
  },
};

function PersonRow({ person, kind, index }) {
  const s = groupStyles[kind];
  const meta =
    kind === "late"
      ? `${person.lateness_minutes} min tarde${person.check_in ? ` · ${person.check_in}` : ""}`
      : kind === "incidents"
      ? person.incident_label
      : kind === "punctual"
      ? `${person.early ? "Entró temprano" : "A tiempo"}${person.check_in ? ` · ${person.check_in}` : ""}`
      : "Sin marca de entrada";

  return (
    <Link
      to={`/empleados/${person.employee_id}`}
      className={`group flex items-center gap-2.5 rounded-full py-1 pl-1 pr-3 ${s.chipBg} hover:bg-white hover:shadow-sm border border-transparent hover:border-zinc-200 transition-all duration-300 animate-in fade-in slide-in-from-bottom-1 zoom-in-95 fill-mode-both`}
      style={{ animationDelay: `${index * 55}ms`, animationDuration: "500ms" }}
      data-testid={`branch-person-${kind}-${person.employee_id}`}
    >
      <span className={`relative w-8 h-8 rounded-full overflow-hidden bg-zinc-100 ring-2 ${s.ring} shrink-0 transition-transform duration-300 group-hover:scale-110`}>
        {person.photo_path ? (
          <AuthImage path={person.photo_path} alt={person.name} className="w-full h-full object-cover" />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-[10px] font-bold text-zinc-400">
            {initialsOf(person.name) || "—"}
          </span>
        )}
        <span className={`absolute -bottom-0 -right-0 w-2.5 h-2.5 rounded-full ${s.dot} ring-2 ring-white`} />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-[13px] font-medium text-zinc-800 leading-tight truncate">{person.name}</span>
          {person.foreign_branch && (
            <span
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 text-[9px] font-semibold px-1.5 py-0.5 leading-none"
              title={`Su sucursal es ${person.home_branch_name || "otra"}`}
              data-testid="branch-foreign-badge"
            >
              <MapPin className="w-2.5 h-2.5" strokeWidth={2.5} />
              {person.home_branch_name || "otra sede"}
            </span>
          )}
        </span>
        <span className={`block text-[11px] leading-tight truncate ${s.text}`}>{meta}</span>
      </span>
    </Link>
  );
}

function Group({ kind, people }) {
  const s = groupStyles[kind];
  const Icon = s.icon;
  const MAX = 6;
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? people : people.slice(0, MAX);
  const extra = people.length - Math.min(people.length, MAX);
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-3.5 h-3.5 ${s.text}`} strokeWidth={2} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-zinc-500">{s.label}</span>
        <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white ${s.dot}`}>
          {people.length}
        </span>
      </div>
      {people.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {visible.map((p, i) => (
            <PersonRow key={p.employee_id} person={p} kind={kind} index={i} />
          ))}
          {extra > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="self-start text-[11px] font-medium text-zinc-400 hover:text-zinc-700 pl-5 pt-0.5 transition-colors cursor-pointer"
              data-testid={`branch-group-toggle-${kind}`}
            >
              {expanded ? "Ver menos" : `+${extra} más`}
            </button>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-zinc-300 pl-5">—</p>
      )}
    </div>
  );
}

function BranchColumn({ branch, index }) {
  const punctual = branch.punctual || [];
  const allClear =
    branch.late.length === 0 &&
    branch.absent.length === 0 &&
    branch.incidents.length === 0 &&
    punctual.length === 0;

  return (
    <div
      className="flex flex-col rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.02)] overflow-hidden animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
      style={{ animationDelay: `${index * 90}ms`, animationDuration: "600ms" }}
      data-testid={`branch-attendance-col-${branch.branch_id}`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center shrink-0">
            <Building2 className="w-3.5 h-3.5 text-white" strokeWidth={1.75} />
          </span>
          <span className="text-lg font-bold tracking-tight text-zinc-900 truncate">
            {branch.branch_name.replace(/^vetcomer\s+/i, "")}
          </span>
        </div>
        <span className="text-[10px] font-medium text-zinc-400 shrink-0">{branch.member_count} activos</span>
      </div>

      <div className="p-4 space-y-4 flex-1">
        {allClear ? (
          <div className="flex flex-col items-center justify-center py-8 text-center animate-in fade-in zoom-in-95 duration-500">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" strokeWidth={1.5} />
            <p className="text-xs text-zinc-500">Todo en orden en esta sucursal</p>
            <p className="text-[10px] text-zinc-400 mt-0.5">Sin tardanzas, ausencias ni incidencias</p>
          </div>
        ) : (
          <>
            <Group kind="late" people={branch.late} />
            <Group kind="absent" people={branch.absent} />
            <Group kind="incidents" people={branch.incidents} />
            <Group kind="punctual" people={punctual} />
          </>
        )}
      </div>
    </div>
  );
}

export default function BranchAttendanceBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get("/dashboard/attendance-by-branch")
        .then((r) => alive && setData(r.data))
        .catch(() => {})
        .finally(() => alive && setLoading(false));
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="branch-attendance-loading">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-56 rounded-2xl bg-zinc-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data.branches?.length) return null;

  return (
    <div className="mb-8" data-testid="branch-attendance-board">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Asistencia de hoy por sucursal
        </span>
        {data.date && (
          <span className="text-[11px] font-semibold text-zinc-600 shrink-0" data-testid="branch-board-date">
            {formatBoardDate(data.date)}
          </span>
        )}
        <div className="flex-1 h-px bg-zinc-200" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.branches.map((b, i) => (
          <BranchColumn key={b.branch_id} branch={b} index={i} />
        ))}
      </div>
    </div>
  );
}
